import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Edit, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { title: "", message: "", target_batch: "all", status: "published" };

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/announcements").then((r) => r.data),
      api.get("/admin/batches").then((r) => r.data),
    ]);
    setItems(a);
    setBatches(b);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/admin/announcements/${editing.id}`, form);
      else await api.post("/admin/announcements", form);
      toast.success("Announcement saved");
      setOpen(false);
      load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete pengumuman?")) return;
    await api.delete(`/admin/announcements/${id}`);
    load();
  };

  return (
    <AdminLayout title="Announcements">
      <div className="flex justify-between items-end gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Announcements</h1>
          <p className="mt-1 text-muted-foreground">Manage announcements for students.</p>
        </div>
        <Button onClick={openAdd} className="btn-glow" data-testid="add-ann-btn"><Plus className="h-4 w-4" /> Add</Button>
      </div>

      <div className="space-y-3">
        {items.map((a) => (
          <Card key={a.id} className="glass-card" data-testid={`admin-ann-${a.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <StatusBadge status={a.status} />
                    <span className="text-xs text-muted-foreground">{a.created_at?.slice(0, 10)}</span>
                    <span className="text-xs text-muted-foreground">· Target: {a.target_batch}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-base">{a.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{a.message}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(a)}><Edit className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(a.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Announcement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-ann-title" /></div>
            <div><Label>Message</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="form-ann-message" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Target</Label>
                <Select value={form.target_batch} onValueChange={(v) => setForm({ ...form, target_batch: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Student</SelectItem>
                    {batches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-ann-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
